import { FC, useId, useMemo, useState } from 'react';

import { CollectionName } from '@/shared/lib/sj-orm/types';
import { Button } from '@/shared/uikitV2/Button';
import { Segmented } from '@/shared/uikitV2/Segmented';
import { Tabs } from '@/shared/uikitV2/Tabs';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { BtnClose } from '../../components/BtnClose';
import { SelectItem } from '../../components/SelectItem';
import {
  AllowedCollectionsSelectDataNames,
  SelectOptions,
} from '../../components/SelectItem/types';
import { useModalsStore } from '../../modals.store';
import { SelectCollectionItemModalProps } from '../../types';

import { CreatePd } from './CreatePd';
import { CreatePid } from './CreatePid';
import { Actions, Body, Container, Header, StyledDrawer, Title } from './styled';

type Mode = 'select' | 'create';

// TODO modal container RightSide
export const SelectCollectionItemModal: FC<SelectCollectionItemModalProps> = ({
  collections,
  onSelect,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'selectCollectionItem' });
  const modals = useModalsStore();

  const defaultTab = (
    collections ? collections[0] : CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS
  ) as AllowedCollectionsSelectDataNames;
  const formId = useId();
  const [tab, setTab] = useState<AllowedCollectionsSelectDataNames>(defaultTab);
  const [mode, setMode] = useState<Mode>('select');
  const [loading, setLoading] = useState(false);

  const mods: { value: Mode; label: string }[] = [
    { value: 'select', label: t('modeAttach') },
    { value: 'create', label: t('modeCreate') },
  ];

  const handleClose = () => modals.closeModal();
  const handleSubmit = (data: SelectOptions) => {
    modals.closeModal();
    onSelect({
      id: Array.isArray(data.id) ? String(data.id[0]) : data.id.toString(),
      colName: data.colName,
    });
  };

  const tabs = useMemo(() => {
    const items = [
      { key: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS, label: 'Personal IDs' },
      { key: CollectionName.PRIVATE_DOCUMENTS, label: 'Personal documents' },
    ];

    return collections ? items.filter((item) => collections.includes(item.key)) : items;
  }, [collections]);

  const BodyComponent = useMemo(() => {
    if (mode === 'select') {
      return <SelectItem formId={formId} colName={tab} onSelect={handleSubmit} />;
    } else if (mode === 'create') {
      switch (tab) {
        case CollectionName.PRIVATE_DOCUMENTS:
          return <CreatePd formId={formId} onChangeState={setLoading} onCreate={handleSubmit} />;
        case CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS:
          return <CreatePid formId={formId} onChangeState={setLoading} onCreate={handleSubmit} />;
        default:
          return <>not implemented</>;
      }
    }

    return <>not implemented</>;
  }, [tab, mode]);

  return (
    <StyledDrawer width={600} footer={null} closable={false} onClose={handleClose} open>
      <Container>
        <Header>
          <BtnClose onClick={handleClose} style={{ position: 'absolute', right: 24, top: 24 }} />
          <Title style={{ maxWidth: '80%' }} className="truncate" size={22} bold>
            {t('title')}
          </Title>
          <Tabs
            fullWidth
            style={{ marginTop: 20 }}
            defaultValue={tab}
            items={tabs}
            onChange={(value) => setTab(value as AllowedCollectionsSelectDataNames)}
          />
        </Header>
        <Body style={{ marginRight: 4 }} className="uikitScrollbar">
          <Segmented
            value={mode}
            options={mods}
            onChange={(value) => setMode(value as Mode)}
            block
          />
          {BodyComponent}
        </Body>
        <Actions>
          <Button type="text" onClick={handleClose}>
            {t('btnCancel')}
          </Button>
          <Button style={{ width: '200px' }} htmlType="submit" form={formId} loading={loading}>
            {mode === 'select' ? t('btnAttach') : t('btnCreate')}
          </Button>
        </Actions>
      </Container>
    </StyledDrawer>
  );
};
